<script setup lang="ts" generic="T extends { id: number | string }">
import type { PaginationResource } from "@carnet/api/backoffice";

const props = defineProps<{
  items: T[];
  pagination: PaginationResource | undefined;
  loading: boolean;
  error?: unknown;
  skeletonCount?: number;
  listClass?: string;
}>();

const page = defineModel<number>("page", { required: true });

defineEmits<{
  refresh: [];
}>();

defineSlots<{
  default(props: { item: T; index: number }): unknown;
  skeleton(): unknown;
  empty?(): unknown;
}>();

const { t } = useI18n();
const listRef = useTemplateRef<HTMLUListElement>("listRef");

usePaginationListFocus(page, () => props.items, listRef);
</script>

<template>
  <ul v-if="loading" aria-hidden="true" :class="listClass">
    <li v-for="i in skeletonCount ?? 12" :key="i">
      <slot name="skeleton" />
    </li>
  </ul>

  <WebErrorRetry v-else-if="error" @retry="$emit('refresh')" />

  <ul
    v-else-if="items.length"
    ref="listRef"
    role="list"
    tabindex="-1"
    :class="listClass"
  >
    <li v-for="(item, index) in items" :key="item.id">
      <slot :item="item" :index="index" />
    </li>
  </ul>

  <slot v-else name="empty" />

  <AppPagination v-if="pagination" v-model:page="page" :pagination="pagination" class="mt-16" />
</template>
